import { useEffect, useRef } from "react";

/**
 * Popcorn overlay — particles "pop" out around the TV area on photo 5 of the gallery,
 * accompanied by a short synthesized popcorn-popping audio (Web Audio API).
 * Auto-stops after ~9 seconds. Triggered by `active` prop becoming true.
 */
export default function Popcorn({ active = false }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (!active) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    let DPR = Math.min(window.devicePixelRatio || 1, 2);
    let W, H;
    const resize = () => {
      W = canvas.width = canvas.clientWidth * DPR;
      H = canvas.height = canvas.clientHeight * DPR;
    };
    resize();
    window.addEventListener("resize", resize);

    const particles = [];

    // TV centre approx — based on photo dims 2160x1440, screen ~(1342,663)-(1631,890)
    // In viewport space we compute on the fly relative to canvas size.
    const tvCenter = () => ({
      x: W * 0.685,
      y: H * 0.54,
    });

    // Spawn a burst of popcorn around TV
    const spawnBurst = (count = 8) => {
      const { x, y } = tvCenter();
      const spreadX = W * 0.16;
      const spreadY = H * 0.10;
      for (let i = 0; i < count; i++) {
        const px = x + (Math.random() - 0.5) * spreadX;
        const py = y + (Math.random() - 0.5) * spreadY;
        particles.push({
          x: px,
          y: py,
          vx: (Math.random() - 0.5) * 8 * DPR,
          vy: -(5 + Math.random() * 7) * DPR,
          rot: Math.random() * Math.PI * 2,
          vr: (Math.random() - 0.5) * 0.25,
          size: (12 + Math.random() * 14) * DPR,
          life: 1,
          decay: 0.006 + Math.random() * 0.005,
          tone: Math.random(),
        });
      }
    };

    // --- Audio: synthesized popcorn pops ---
    let audioCtx = null;
    try {
      audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    } catch (e) {
      audioCtx = null;
    }

    const playPop = () => {
      if (!audioCtx) return;
      try {
        const t = audioCtx.currentTime;
        // Short, sharp burst — band-passed noise + quick decay
        const buffer = audioCtx.createBuffer(1, audioCtx.sampleRate * 0.12, audioCtx.sampleRate);
        const data = buffer.getChannelData(0);
        for (let i = 0; i < data.length; i++) {
          const env = Math.exp(-i / (audioCtx.sampleRate * 0.025));
          data[i] = (Math.random() * 2 - 1) * env * 0.9;
        }
        const src = audioCtx.createBufferSource();
        src.buffer = buffer;
        const filt = audioCtx.createBiquadFilter();
        filt.type = "bandpass";
        filt.frequency.value = 1200 + Math.random() * 1800;
        filt.Q.value = 8 + Math.random() * 6;
        const gain = audioCtx.createGain();
        gain.gain.setValueAtTime(0.0, t);
        gain.gain.linearRampToValueAtTime(0.5 + Math.random() * 0.4, t + 0.005);
        gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.12);
        src.connect(filt).connect(gain).connect(audioCtx.destination);
        src.start(t);
        src.stop(t + 0.12);
      } catch (e) {}
    };

    let rafId;
    let active2 = true;
    const startTime = performance.now();
    let lastBurst = 0;
    let burstInterval = 350;

    function drawKernel(p) {
      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.rotate(p.rot);
      ctx.globalAlpha = Math.min(p.life, 1);

      const r = p.size;
      const tone = p.tone;

      // Body - fluffy lobes (4 circles)
      const main = `rgb(${250 + tone * 5 | 0}, ${235 + tone * 10 | 0}, ${190 + tone * 30 | 0})`;
      const shadow = `rgb(${220 + tone * 20 | 0}, ${190 + tone * 25 | 0}, ${130 + tone * 30 | 0})`;
      ctx.fillStyle = shadow;
      const lobes = [
        { x: 0, y: -r * 0.18, rr: r * 0.55 },
        { x: r * 0.30, y: r * 0.04, rr: r * 0.50 },
        { x: -r * 0.28, y: r * 0.10, rr: r * 0.50 },
        { x: 0, y: r * 0.30, rr: r * 0.45 },
      ];
      lobes.forEach((l) => {
        ctx.beginPath();
        ctx.arc(l.x + 1.5, l.y + 1.5, l.rr, 0, Math.PI * 2);
        ctx.fill();
      });
      ctx.fillStyle = main;
      lobes.forEach((l) => {
        ctx.beginPath();
        ctx.arc(l.x, l.y, l.rr, 0, Math.PI * 2);
        ctx.fill();
      });

      // Tiny golden kernel hint in center
      ctx.fillStyle = "rgba(210,160,80,0.45)";
      ctx.beginPath();
      ctx.arc(0, r * 0.08, r * 0.15, 0, Math.PI * 2);
      ctx.fill();

      ctx.restore();
    }

    function tick(t) {
      if (!active2) return;
      const age = t - startTime;
      if (age > 9000) {
        stop();
        return;
      }
      // Spawn bursts
      if (t - lastBurst > burstInterval) {
        spawnBurst(6 + Math.floor(Math.random() * 6));
        // Play 2-4 pops slightly staggered
        const pops = 2 + Math.floor(Math.random() * 3);
        for (let i = 0; i < pops; i++) {
          setTimeout(playPop, i * 60);
        }
        lastBurst = t;
        burstInterval = 280 + Math.random() * 320;
      }

      ctx.clearRect(0, 0, W, H);

      for (let i = particles.length - 1; i >= 0; i--) {
        const p = particles[i];
        p.x += p.vx;
        p.y += p.vy;
        p.vy += 0.28 * DPR; // gravity
        p.vx *= 0.99;
        p.rot += p.vr;
        p.life -= p.decay;
        if (p.life <= 0 || p.y > H + 50) {
          particles.splice(i, 1);
          continue;
        }
        drawKernel(p);
      }

      rafId = requestAnimationFrame(tick);
    }

    function stop() {
      if (!active2) return;
      active2 = false;
      cancelAnimationFrame(rafId);
      if (audioCtx) {
        setTimeout(() => audioCtx && audioCtx.close && audioCtx.close(), 1000);
      }
    }

    // Resume audio context on first user gesture (already inside a click handler so it should work)
    if (audioCtx && audioCtx.state === "suspended") {
      audioCtx.resume().catch(() => {});
    }

    rafId = requestAnimationFrame(tick);

    return () => {
      stop();
      window.removeEventListener("resize", resize);
    };
  }, [active]);

  if (!active) return null;

  return (
    <canvas
      ref={canvasRef}
      data-testid="popcorn-canvas"
      className="pointer-events-none absolute inset-0 z-[110] h-full w-full"
      aria-hidden="true"
    />
  );
}
